import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type Context,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AuthContext as PluginAuthContext, type PluginAuthState } from '@reflex/ui/client/context';
import {
  api,
  ApiError,
  checkAuth,
  clearAuthToken,
  DEV_LOGIN_AUTO_SUPPRESS_KEY,
  getActiveOrganizationId,
  getAuthToken,
  markSessionEnded,
  getActiveOrgSlugFromUrl,
  setActiveOrganizationId as writeActiveOrgId,
} from '@/lib/api';
import type { AuthMembership, AuthProviders, AuthStatus, DevLoginStatus } from '@/lib/api';
import type { User } from '@/lib/types';
import { identifyUser, identifyOrganization, clearActiveGroups, resetPostHog } from '@/lib/posthog';
import { getErrorReporter } from '@/lib/error-reporter';

/**
 * How long the tab-visibility refresh waits before it will re-read
 * `/auth/me` again.
 *
 * The point of the listener is to bound permission staleness to a tab
 * switch, not to poll. Without a floor, alt-tabbing between two windows
 * issues one uncached six-query `/auth/me` per switch, per open tab, on top
 * of the app-wide `refetchOnWindowFocus`. Ten seconds collapses a burst of
 * switches into one read while keeping "your new role applies when you come
 * back to the tab" true in any human sense. It does NOT apply to the mount
 * refresh or to an explicit `refreshSession()` from a mutation.
 */
const VISIBILITY_REFRESH_MIN_INTERVAL_MS = 10_000;

/**
 * Delays between automatic `/auth/me` retries while the session is
 * unreadable, in order, holding at the last entry. Starts fast because a
 * server restart is usually over in seconds, and settles at half a minute so
 * a tab left on the connection-error screen overnight costs almost nothing.
 * Only ever runs while {@link AuthState.connectionError} is set.
 */
const RECONNECT_BACKOFF_MS = [2_000, 5_000, 15_000, 30_000];

function trackAuthenticatedUser(
  user: User | null,
  activeOrganization?: { id: string; name?: string; slug?: string } | null,
) {
  if (user) {
    getErrorReporter().setUser({ id: user.id });
    identifyUser(user);
    if (activeOrganization) {
      identifyOrganization(activeOrganization);
    } else {
      clearActiveGroups();
    }
  } else {
    getErrorReporter().setUser(null);
    clearActiveGroups();
    resetPostHog();
  }
}

export interface AuthState {
  authenticated: boolean;
  authEnabled: boolean;
  loading: boolean;
  user: User | null;
  providers: AuthProviders;
  memberships: AuthMembership[];
  ssoRequirements?: Array<{
    organizationId: string;
    organizationSlug: string;
    organizationName: string;
    satisfied: boolean;
  }>;
  activeOrganizationId: string | null;
  activeMembership: AuthMembership | null;
  /**
   * Org IDs the user has membership on but where the org has never
   * been bootstrapped AND the user holds `org:manage`. AuthGate uses
   * this to send the user into `/orgs/:orgSlug/setup` before any other
   * surface mounts.
   */
  orgsNeedingBootstrap: string[];
  permissions: Set<string>;
  /**
   * Literal `users.is_super_admin` flag. Prefer {@link isPlatformAdmin}
   * for UI gating — it's the union of super admin and the assignable
   * platform-admin role and is what the server uses for bypass decisions.
   */
  isSuperAdmin: boolean;
  /**
   * True when the user holds the built-in `platform-admin` role on their
   * SYSTEM_ORG membership. Granted via `/admin/app-admins`.
   */
  isAppAdmin: boolean;
  /**
   * Derived `isSuperAdmin || isAppAdmin`. The single bit every admin-only
   * UI gate (admin nav, admin routes, etc.) should consult.
   */
  isPlatformAdmin: boolean;
  /**
   * Platform-wide policy: `false` when an admin has turned off self-serve
   * organization creation. Prefer {@link canCreateOrganization} for UI
   * gating — it also accounts for the platform-admin bypass.
   */
  allowSelfServeOrgCreation: boolean;
  /**
   * Derived `allowSelfServeOrgCreation || isPlatformAdmin`. False means the
   * create-org surfaces should explain that creation is turned off rather
   * than render a form the server answers with 403.
   */
  canCreateOrganization: boolean;
  /**
   * True when the last `/auth/me` read failed in a way that leaves us
   * unable to say whether the visitor is signed in: the server was
   * unreachable on first load, or it answered with something that is not an
   * `AuthStatus` (a 500, a proxy's HTML error page). Distinguishes "we could
   * not ask" from "the server says you are signed out", so the login screen
   * can offer a retry instead of reporting the empty provider list that a
   * failed read leaves behind as a configuration problem.
   */
  connectionError: boolean;
  /** True until the first-run setup wizard finishes. */
  setupRequired: boolean;
  /** Mirrors the server's NODE_ENV !== 'production' — shapes the wizard. */
  isProductionEnv: boolean;
  /**
   * Set when the server has `/auth/dev-login` mounted (dev and seeded
   * preview servers only — never production). The login screen renders a
   * one-click dev sign-in button per advertised identity, and signs in as
   * `autoLoginEmail` automatically when the server sets it.
   */
  devLogin?: DevLoginStatus;
  can: (permission: string) => boolean;
  setActiveOrganizationId: (id: string | null) => void;
  logout: () => Promise<void>;
  refreshSession: (opts?: { preferredActiveOrgId?: string | null }) => Promise<void>;
}

export const AuthContext = PluginAuthContext as Context<(AuthState & PluginAuthState) | null>;

interface AuthData {
  authenticated: boolean;
  authEnabled: boolean;
  loading: boolean;
  user: User | null;
  providers: AuthProviders;
  memberships: AuthMembership[];
  ssoRequirements: NonNullable<AuthState['ssoRequirements']>;
  activeOrganizationId: string | null;
  orgsNeedingBootstrap: string[];
  isSuperAdmin: boolean;
  isAppAdmin: boolean;
  allowSelfServeOrgCreation: boolean;
  connectionError: boolean;
  setupRequired: boolean;
  isProductionEnv: boolean;
  devLogin?: DevLoginStatus;
}

type AuthAction =
  | {
      type: 'loaded';
      authenticated: boolean;
      authEnabled: boolean;
      user: User | null;
      providers: AuthProviders;
      memberships: AuthMembership[];
      ssoRequirements: NonNullable<AuthState['ssoRequirements']>;
      activeOrganizationId: string | null;
      orgsNeedingBootstrap: string[];
      isSuperAdmin: boolean;
      isAppAdmin: boolean;
      allowSelfServeOrgCreation: boolean;
      setupRequired: boolean;
      isProductionEnv: boolean;
      devLogin?: DevLoginStatus;
    }
  | { type: 'unauthorized' }
  | { type: 'unreachable' }
  | { type: 'active_org_changed'; activeOrganizationId: string | null };

/**
 * What the session looks like when `/auth/me` could not be read at all.
 *
 * Everything is cleared (nothing here is trustworthy) and `connectionError`
 * marks *why* it is empty, so the login screen does not present a provider
 * list we never managed to fetch as "no providers are configured".
 */
const UNREACHABLE_STATUS: AuthData = {
  loading: false,
  authenticated: false,
  authEnabled: true,
  user: null,
  providers: {},
  memberships: [],
  ssoRequirements: [],
  activeOrganizationId: null,
  orgsNeedingBootstrap: [],
  isSuperAdmin: false,
  isAppAdmin: false,
  allowSelfServeOrgCreation: true,
  connectionError: true,
  setupRequired: false,
  isProductionEnv: false,
  devLogin: undefined,
};

const initialAuthData: AuthData = {
  authenticated: false,
  authEnabled: true,
  loading: true,
  user: null,
  providers: {},
  memberships: [],
  ssoRequirements: [],
  activeOrganizationId: null,
  orgsNeedingBootstrap: [],
  isSuperAdmin: false,
  isAppAdmin: false,
  allowSelfServeOrgCreation: true,
  connectionError: false,
  setupRequired: false,
  isProductionEnv: false,
  devLogin: undefined,
};

function authReducer(state: AuthData, action: AuthAction): AuthData {
  switch (action.type) {
    case 'loaded':
      return {
        authenticated: action.authenticated,
        authEnabled: action.authEnabled,
        loading: false,
        user: action.user,
        providers: action.providers,
        memberships: action.memberships,
        ssoRequirements: action.ssoRequirements,
        activeOrganizationId: action.activeOrganizationId,
        orgsNeedingBootstrap: action.orgsNeedingBootstrap,
        isSuperAdmin: action.isSuperAdmin,
        isAppAdmin: action.isAppAdmin,
        allowSelfServeOrgCreation: action.allowSelfServeOrgCreation,
        connectionError: false,
        setupRequired: action.setupRequired,
        isProductionEnv: action.isProductionEnv,
        devLogin: action.devLogin,
      };
    case 'unreachable':
      return UNREACHABLE_STATUS;
    case 'unauthorized':
      return {
        ...state,
        authenticated: false,
        // Only `unreachable` may claim we could not read the session.
        connectionError: false,
        user: null,
        memberships: [],
        orgsNeedingBootstrap: [],
        isSuperAdmin: false,
        isAppAdmin: false,
      };
    case 'active_org_changed':
      return { ...state, activeOrganizationId: action.activeOrganizationId };
  }
}

export function useAuthProvider() {
  const [state, dispatch] = useReducer(authReducer, initialAuthData);
  const queryClient = useQueryClient();

  // Monotonic token so only the most recently *requested* refresh is allowed
  // to commit. Org switching fires a pinned refresh per switch; if an older
  // refresh (pinned to the org you just left) resolves after a newer one, it
  // must NOT overwrite the newer result — otherwise it yanks `activeOrganizationId`
  // back to the previous org and `OrgGate` bounces the user to that org's setup.
  const refreshSeq = useRef(0);

  // Latest-state ref so `setActiveOrganizationId` can read the current active
  // org / memberships WITHOUT listing them as `useCallback` deps. Its identity
  // MUST stay stable across org switches: route guards like
  // `OrgSetupExistingRoute` depend on it in an effect, and if its identity
  // changed on every switch that effect would re-run and re-assert the org you
  // just left — bouncing you straight back to that org's setup wizard.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // How many org-switch (pinned) refreshes are in flight. A count rather
  // than a boolean so two rapid switches can't have the first one's
  // completion clear the flag while the second is still running. Read by
  // the tab-visibility listener, which must stay out of a switch's way.
  const pinnedRefreshes = useRef(0);

  // When the tab-visibility listener last issued a refresh, for the
  // {@link VISIBILITY_REFRESH_MIN_INTERVAL_MS} floor. Starts at 0 so the
  // first tab switch after mount always re-reads.
  const lastVisibilityRefreshAt = useRef(0);

  // Identity last reported to PostHog / the error reporter, so a refresh
  // that changed nothing (the common case for the visibility listener)
  // doesn't re-`identify` on every tab focus in every open tab.
  const lastTrackedIdentity = useRef<string | null>(null);
  const trackIdentityIfChanged = useCallback(
    (
      user: User | null,
      activeOrganization?: { id: string; name?: string; slug?: string } | null,
    ) => {
      const key = user ? `${user.id}:${activeOrganization?.id ?? ''}` : 'signed-out';
      if (lastTrackedIdentity.current === key) return;
      lastTrackedIdentity.current = key;
      trackAuthenticatedUser(user, activeOrganization);
    },
    [],
  );

  const refreshInternal = useCallback(
    async (opts?: { preferredActiveOrgId?: string | null }) => {
      const seq = ++refreshSeq.current;
      let status: AuthStatus;
      try {
        status = await checkAuth();
      } catch (err) {
        if (seq !== refreshSeq.current) return;
        // Two different failures arrive here, and only one of them is a
        // reason to keep the session.
        //
        // No response at all (DNS, offline, aborted, TLS) says nothing about
        // whether the session is valid. This runs on every tab focus, so
        // treating a blip as a sign-out would drop the user to the login
        // screen whenever they came back to a tab on a flaky network. Keep
        // what we have — except on the very first load, which has nothing to
        // keep and where the login screen is the right fallback.
        //
        // An `ApiError` is the opposite: the server answered, and the answer
        // was one `checkAuth` could not read as an `AuthStatus` (a 401 whose
        // body is a proxy's HTML, a 500, a 502 from a load balancer). That is
        // not transient, and it must NOT leave the tab rendering an
        // authenticated shell against the permission snapshot it took at
        // mount — every affordance gate in the app reads that snapshot. The
        // SDK's global 401 handler deliberately skips `/auth/*`
        // (`ui/src/client/http.ts` `onUnauthorized`), so nothing else will
        // sign the user out on our behalf. Fail closed.
        //
        // Either way we never learned the visitor's auth status, so the
        // result is `unreachable`, not "signed out": the login screen reads
        // `connectionError` and offers a retry rather than reporting the
        // never-fetched provider list as a server misconfiguration.
        if (err instanceof ApiError || stateRef.current.loading) {
          dispatch({ type: 'unreachable' });
          trackIdentityIfChanged(null);
        }
        return;
      }
      try {
        // A newer refresh was requested while this one was in flight — drop this
        // result so the latest switch wins.
        if (seq !== refreshSeq.current) return;
        // The SDK's global 401 handler skips `/auth/*`, so a stored bearer
        // that `/auth/me` no longer honours (expired while the tab was
        // closed, revoked, swept) is noticed here and nowhere else. Drop it
        // and leave the same "session ended" note the 401 path leaves, so
        // the login screen reads the same either way.
        if (!status.authenticated && getAuthToken()) {
          markSessionEnded();
          clearAuthToken();
        }
        const memberships = status.memberships ?? [];
        // URL is the source of truth (see OrgSlugResolver). On a hard load of
        // `/runloop/agents` we must seed the active org from the slug, not
        // from the previous session's localStorage value — otherwise the
        // shell, the org switcher, and any HTTP/WS request issued before
        // `OrgSlugResolver`'s effect commits will all be scoped to the wrong
        // org. Falls back to localStorage / server hint / first membership
        // for root-level routes (`/profile`, `/admin/*`) with no slug.
        const urlSlug = getActiveOrgSlugFromUrl();
        const storedOrg = getActiveOrganizationId();
        const serverOrg = status.activeOrganizationId ?? null;
        const fallback = memberships.length > 0 ? memberships[0].organization.id : null;
        const urlMembership = urlSlug
          ? memberships.find(
              (m) => m.organization.slug === urlSlug || m.organization.id === urlSlug,
            )
          : undefined;
        const membershipIds = new Set(memberships.map((m) => m.organization.id));
        // An explicit switch target wins over the URL/stored/server chain.
        // `setActiveOrganizationId` fires this refresh from the org switcher
        // *before* the route has navigated to the new org's slug, so the
        // still-current URL would otherwise re-derive the previous org and
        // fight the switch (bouncing the user back to where they started).
        const preferred = opts?.preferredActiveOrgId;
        const preferredActive = preferred && membershipIds.has(preferred) ? preferred : null;
        const activeOrganizationId =
          preferredActive ||
          urlMembership?.organization.id ||
          (storedOrg && membershipIds.has(storedOrg) && storedOrg) ||
          (serverOrg && membershipIds.has(serverOrg) && serverOrg) ||
          fallback;
        if (activeOrganizationId !== storedOrg) {
          writeActiveOrgId(activeOrganizationId);
        }
        dispatch({
          type: 'loaded',
          authenticated: status.authenticated,
          authEnabled: status.authEnabled,
          user: status.user ?? null,
          providers: status.providers ?? {},
          memberships,
          ssoRequirements: status.ssoRequirements ?? [],
          activeOrganizationId,
          orgsNeedingBootstrap: status.orgsNeedingBootstrap ?? [],
          isSuperAdmin: Boolean(status.isSuperAdmin),
          isAppAdmin: Boolean(status.isAppAdmin),
          // An older server omits the field entirely; absent means the
          // platform still allows self-serve creation.
          allowSelfServeOrgCreation: status.allowSelfServeOrgCreation !== false,
          setupRequired: Boolean(status.setupRequired),
          isProductionEnv: Boolean(status.isProduction),
          devLogin: status.devLogin,
        });
        const activeOrg = activeOrganizationId
          ? memberships.find((m) => m.organization.id === activeOrganizationId)?.organization
          : null;
        trackIdentityIfChanged(status.user ?? null, activeOrg ?? null);
      } catch {
        // Reached the server but could not make sense of the answer. Drop
        // the stale identity rather than leaving it on screen, and mark the
        // session unreadable so the login screen says so.
        if (seq !== refreshSeq.current) return;
        dispatch({ type: 'unreachable' });
        trackIdentityIfChanged(null);
      }
    },
    [trackIdentityIfChanged],
  );

  /**
   * Re-read the session. `preferredActiveOrgId` pins the result to an
   * org-switch target (see `setActiveOrganizationId`).
   *
   * The wrapper exists to track pinned refreshes: a switch is the one
   * window where an *unpinned* refresh is actively harmful, because it
   * would bump `refreshSeq`, discard the switch's in-flight result, and
   * then re-derive the active org from a URL that has not navigated yet —
   * landing the user back in the org they just left. The tab-visibility
   * listener below is the only unpinned caller that can fire at an
   * arbitrary moment, so it consults this counter.
   */
  const refreshSession = useCallback(
    async (opts?: { preferredActiveOrgId?: string | null }) => {
      const pinned = opts?.preferredActiveOrgId != null;
      if (pinned) pinnedRefreshes.current += 1;
      try {
        await refreshInternal(opts);
      } finally {
        if (pinned) pinnedRefreshes.current -= 1;
      }
    },
    [refreshInternal],
  );

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    const handle = () => {
      dispatch({ type: 'unauthorized' });
      trackIdentityIfChanged(null);
    };
    window.addEventListener('auth:unauthorized', handle);
    return () => window.removeEventListener('auth:unauthorized', handle);
  }, [trackIdentityIfChanged]);

  // `memberships[].permissions` drives every affordance gate in the UI, and
  // this reducer is not React Query, so no cache invalidation can reach it —
  // only `refreshSession()` can. A role change made by an admin in another tab (or
  // by anyone else) would otherwise leave this tab gated on a snapshot taken
  // at mount, for the life of the tab. Re-fetching when the tab becomes
  // visible bounds that staleness to one tab switch.
  //
  // `visibilitychange` alone, not `visibilitychange` + `focus`: both fire on
  // a tab switch, and `/auth/me` is an uncached six-query read, so listening
  // to each would double its rate for no extra freshness.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // An org switch is mid-flight and already fetching. Joining in would
      // cancel its pinned result and re-derive the org from the pre-switch
      // URL — see `refreshSession` above.
      if (pinnedRefreshes.current > 0) return;
      const now = Date.now();
      if (now - lastVisibilityRefreshAt.current < VISIBILITY_REFRESH_MIN_INTERVAL_MS) return;
      lastVisibilityRefreshAt.current = now;
      void refreshSession();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSession]);

  // A visitor stuck on the connection-error screen has nothing else to read
  // and no other affordance: `/auth/me` never answered, so there are no
  // providers to sign in with. Retry for them rather than making the way out
  // a click they have to keep guessing at.
  //
  // Both triggers are needed, because the two things that put them here fail
  // differently. A dropped network resolves with an `online` event and
  // should be picked up the instant it fires; a server that is restarting or
  // answering 502 never fires one (`navigator.onLine` stayed true the whole
  // time), so only the backoff finds it. Registered solely while we are
  // stuck — everywhere else the mount and visibility refreshes cover it, and
  // this must never become a poll of a healthy session.
  useEffect(() => {
    if (!state.connectionError) return;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout>;
    const scheduleRetry = () => {
      const delay = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)] ?? 0;
      attempt += 1;
      // A successful refresh clears `connectionError`, which re-runs this
      // effect and its cleanup cancels whatever the `finally` just queued.
      timer = setTimeout(() => void refreshSession().finally(scheduleRetry), delay);
    };
    scheduleRetry();
    const onOnline = () => void refreshSession();
    window.addEventListener('online', onOnline);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('online', onOnline);
    };
  }, [state.connectionError, refreshSession]);

  const logout = useCallback(async () => {
    // An explicit sign-out must win over a seeded preview's auto sign-in
    // (`devLogin.autoLoginEmail`), or the login screen would immediately sign
    // the user back in and identity switching would be impossible.
    sessionStorage.setItem(DEV_LOGIN_AUTO_SUPPRESS_KEY, '1');
    try {
      await api.logout();
    } catch {
      // Best-effort server logout; clear local state regardless
    }
    queryClient.removeQueries({ queryKey: ['activity'] });
    dispatch({ type: 'unauthorized' });
    trackAuthenticatedUser(null);
    // Confirm the sign-out to the user. The <Toaster /> lives at the app root
    // so the toast survives the redirect to the login screen that follows.
    toast.success('Signed out');
  }, [queryClient]);

  const setActiveOrganizationId = useCallback(
    (id: string | null) => {
      // Read live state from the ref (NOT closure/deps) so this callback's
      // identity stays stable — see `stateRef` above.
      const { activeOrganizationId: prevActive, memberships } = stateRef.current;
      // A real switch = moving between two already-resolved orgs (not the
      // initial null → first-org seed, which the mount `refreshSession()` already
      // fetched fresh, and not a redundant set to the same org). Only those
      // need a re-fetch below.
      const isSwitch = id != null && prevActive != null && id !== prevActive;
      writeActiveOrgId(id);
      dispatch({ type: 'active_org_changed', activeOrganizationId: id });
      if (id) {
        const org = memberships.find((m) => m.organization.id === id)?.organization;
        if (org) {
          identifyOrganization(org);
        }
      } else {
        clearActiveGroups();
      }
      queryClient.invalidateQueries();
      // Re-fetch `/auth/me` on switch so per-org fields reflect current
      // server state. The memberships snapshot (including each org's
      // `bootstrappedAt`) is otherwise frozen at app mount — an org that was
      // bootstrapped after this tab loaded keeps a stale `bootstrappedAt:
      // null`, and `OrgGate` wrongly traps the user in `/orgs/:orgSlug/setup`
      // instead of letting them into the app. Pin the switch target so the
      // in-flight refresh doesn't re-derive the previous org from the URL
      // (which hasn't navigated yet when the wizard switcher fires).
      if (isSwitch) {
        void refreshSession({ preferredActiveOrgId: id });
      }
    },
    [queryClient, refreshSession],
  );

  const activeMembership = useMemo(() => {
    if (!state.activeOrganizationId) return null;
    return state.memberships.find((m) => m.organization.id === state.activeOrganizationId) ?? null;
  }, [state.memberships, state.activeOrganizationId]);

  const permissions = useMemo(
    () => new Set(activeMembership?.permissions ?? []),
    [activeMembership],
  );

  const can = useCallback((permission: string) => permissions.has(permission), [permissions]);

  const isPlatformAdmin = state.isSuperAdmin || state.isAppAdmin;
  // Platform admins keep their server-side bypass of the self-serve gate, so
  // the UI must not hide the affordance from them.
  const canCreateOrganization = state.allowSelfServeOrgCreation || isPlatformAdmin;

  return {
    ...state,
    activeMembership,
    permissions,
    isPlatformAdmin,
    canCreateOrganization,
    can,
    setActiveOrganizationId,
    logout,
    refreshSession,
  };
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
